import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDndContext, useDroppable } from '@dnd-kit/core';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  MousePointerClick,
  Package,
  QrCode,
  UserPlus,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import { allocationsApi, stockApi } from '@/features/stock/api';
import { eventsApi } from '@/features/events/api';
import { reporterAdminApi, type ReporterListRow } from '@/features/reporter/adminApi';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkEvent, XkEventStockStation } from '@/lib/api/types';
import { toast } from '@/components/ui/Toast';
import { IconButtonGroup, IconButtonGroupItem } from '@/components/ui/IconButtonGroup';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { TagFilter } from '@/components/ui/TagFilter';
import { EntityTags } from '@/features/tags/EntityTags';
import { useTagAssignments, useTagMatch, useTags } from '@/lib/api/tags';
import { eventPhase } from '../eventPhase';
import { ReporterAvatar } from './ReporterAvatar';
import { StationCard } from './StationAllocations';
import { GameGlanceChip } from './GameGlanceChip';

type StockLite = { name: string; image?: string | null; quantityRemaining?: number | null; rating?: number | null };

/**
 * PLAN tab, left column. Collapsible list of plannable events. Each row is a
 * drop target for xotskis / reporters / stations and, when expanded, lets the
 * manager tune what's attached: bump a xotski's quantity (within brand
 * availability) or remove it, and remove a reporter. Both strips lay out
 * horizontally so a growing list never pushes the editor down.
 */
/** Etiquetas: el filtro vive aquí, y las píldoras dentro de cada fila. */
export function EditableEventList({
  groupId,
  brandId,
  events,
  selectedEventId,
  onSelect,
  focusEventId,
  hideOpenConfig = false,
  autoScrollToFocus = true,
  onRepartirTodo,
  onOpenProperties,
}: {
  groupId: number;
  brandId: number;
  events: XkEvent[];
  selectedEventId: number | null;
  onSelect: (id: number) => void;
  /**
   * Abrir las propiedades a mano. En un evento EN CURSO seleccionar la fila
   * ya no las abre solo (el carril es para el monitor), así que la fila
   * enseña un botón explícito.
   */
  onOpenProperties?: (id: number) => void;
  focusEventId?: number | null;
  /**
   * Traer a la vista el evento enfocado al montar.
   *
   * Tiene sentido en el proyecto, donde la lista trae muchos y el enfocado
   * puede estar por debajo del pliegue. **No lo tiene en la página del
   * evento**, donde sólo hay uno y ES el asunto de la página: allí el
   * desplazamiento abría la pantalla a media altura, con el título y las
   * pestañas ya fuera, y quien entraba no sabía dónde estaba.
   */
  autoScrollToFocus?: boolean;
  /** Hide the per-row "Open config" link (e.g. on the event page itself). */
  hideOpenConfig?: boolean;
  /** Repartir los premios de la marca en un evento, sin arrastrar. */
  onRepartirTodo?: (eventId: number) => void | Promise<void>;
}) {
  const t = useT();

  /*
    Etiquetas de evento. El filtro se aplica ANTES de agrupar por proyecto: si
    se filtrara después, quedarían encabezados de proyecto sin una sola fila
    debajo.
  */
  const [filtroTags, setFiltroTags] = useState<number[]>([]);
  const vocabTags = useTags(brandId, 'event').data ?? [];
  const { ids: casanTags } = useTagMatch(brandId, 'event', filtroTags);

  // `null` = sin filtro; `[]` = nada cumple. No es lo mismo.
  const eventosVisibles = useMemo(
    () => (casanTags == null ? events : events.filter((e) => casanTags.includes(e.id))),
    [events, casanTags],
  );

  const asignTags =
    useTagAssignments(
      brandId,
      'event',
      useMemo(() => eventosVisibles.map((e) => e.id), [eventosVisibles]),
    ).data ?? {};

  const stockQ = useQuery({
    queryKey: queryKeys.stock.byBrand(brandId),
    queryFn: () => stockApi.list({ companyId: brandId }),
    enabled: brandId > 0,
  });
  const stockMap = useMemo(() => {
    const m = new Map<number, StockLite>();
    for (const s of stockQ.data?.items ?? [])
      m.set(s.id, { name: s.name, image: s.image, quantityRemaining: s.quantityRemaining, rating: s.rating });
    return m;
  }, [stockQ.data]);

  // Group names for the headings. Cheap: the brand's groups are already in
  // cache from the projects list on the way in.
  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId),
    queryFn: () => eventsApi.listGroups({ companyId: brandId }),
    enabled: brandId > 0,
  });

  /**
   * Events bucketed by their event group, preserving the incoming order both
   * between groups and inside each one (the caller has already sorted by date).
   */
  const grouped = useMemo(() => {
    const nameById = new Map<number, string>();
    for (const g of groupsQ.data?.items ?? []) nameById.set(g.id, g.name);

    const order: number[] = [];
    const byGroup = new Map<number, XkEvent[]>();
    for (const e of eventosVisibles) {
      const gid = e.eventGroupId ?? groupId;
      if (!byGroup.has(gid)) {
        byGroup.set(gid, []);
        order.push(gid);
      }
      byGroup.get(gid)!.push(e);
    }
    return order.map((id) => ({
      id,
      name: nameById.get(id) ?? t('planner.events.title'),
      rows: byGroup.get(id)!,
    }));
  }, [eventosVisibles, groupsQ.data, groupId, t]);

  if (events.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] p-6 text-center text-sm text-[var(--color-xk-text-muted)]">
        {t('planner.events.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <TagFilter
        vocabulario={vocabTags}
        valor={filtroTags}
        onChange={setFiltroTags}
        resultado={eventosVisibles.length}
      />
      {grouped.map(({ id, name, rows }) => (
        <section key={id} className="space-y-2">
          {/* Task 3.2 — events sit under their event-group heading. In Xotski
              an event group IS the project, so on a project board this is one
              heading; the grouping is computed rather than assumed so a list
              spanning several projects renders correctly without changes. */}
          <h4 className="flex items-baseline gap-2 border-b border-[var(--color-border-subtle)] pb-1 text-[11px] font-extrabold tracking-wide text-[var(--color-fg-muted)]">
            <span className="truncate">{name}</span>
            <span className="tabular-nums text-[var(--color-fg-subtle)]">{rows.length}</span>
          </h4>
          {rows.map((e) => (
        <EventRow
          key={e.id}
          groupId={groupId}
          brandId={brandId}
          event={e}
          stockMap={stockMap}
          selected={selectedEventId === e.id}
          onSelect={onSelect}
          {...(onOpenProperties ? { onOpenProperties } : {})}
              autoExpand={focusEventId === e.id}
              autoScrollToFocus={autoScrollToFocus}
              hideOpenConfig={hideOpenConfig}
              {...(onRepartirTodo ? { onRepartirTodo } : {})}
              etiquetas={
                <EntityTags
                  companyId={brandId}
                  entityType="event"
                  entityId={e.id}
                  vocabulario={vocabTags}
                  valor={asignTags[e.id] ?? []}
                />
              }
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function EventRow({
  groupId,
  brandId,
  event,
  stockMap,
  selected,
  onSelect,
  autoExpand,
  autoScrollToFocus,
  hideOpenConfig,
  etiquetas,
  onRepartirTodo,
  onOpenProperties,
}: {
  groupId: number;
  brandId: number;
  event: XkEvent;
  stockMap: Map<number, StockLite>;
  selected: boolean;
  onSelect: (id: number) => void;
  onOpenProperties?: (id: number) => void;
  autoExpand: boolean;
  autoScrollToFocus: boolean;
  hideOpenConfig?: boolean;
  /** Repartir los premios de la marca en este evento, sin arrastrar. */
  onRepartirTodo?: (eventId: number) => void | Promise<void>;
  /**
   * El selector de etiquetas, ya cableado por la lista.
   *
   * Llega montado en vez de que la fila lo construya porque el vocabulario y
   * las asignaciones se piden UNA vez para toda la lista: si cada fila los
   * pidiera, una lista de cien eventos haría doscientas peticiones.
   */
  etiquetas?: React.ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(autoExpand);
  const rowRef = useRef<HTMLDivElement>(null);

  const { setNodeRef, isOver } = useDroppable({
    id: `event-${event.id}`,
    data: { eventId: event.id },
  });
  // True while ANY item is being dragged — used to light up every event as a
  // candidate drop zone so the user sees where things can land.
  const dragging = !!useDndContext().active;

  useEffect(() => {
    if (autoExpand) {
      setOpen(true);
      onSelect(event.id);
      // El desplazamiento SÓLO donde hay de dónde desplazarse. Ver
      // `autoScrollToFocus` en la lista: en la página del evento este
      // `scrollIntoView` abría la pantalla ya empezada.
      if (autoScrollToFocus) {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpand]);

  const allocationsQ = useQuery({
    queryKey: queryKeys.stockAllocations.byEvent(event.id),
    queryFn: () => allocationsApi.list({ eventId: event.id }),
    enabled: open,
  });
  const reportersQ = useQuery({
    queryKey: queryKeys.reporters.byEvent(event.id),
    queryFn: () => reporterAdminApi.listForEvent(event.id),
    enabled: open,
    // A reporter confirms on THEIR device (accepts the magic-link terms). Poll
    // while this event is expanded — and refetch on window focus — so the
    // Pending → Confirmed flip shows up here without a manual reload.
    refetchInterval: open ? 15_000 : false,
    refetchOnWindowFocus: true,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byEvent(event.id),
    queryFn: () => eventsApi.listStations({ eventId: event.id }),
    enabled: open,
  });

  const allocations = allocationsQ.data?.items ?? [];
  const reporters = reportersQ.data ?? [];
  const stations = stationsQ.data?.items ?? [];
  const phase = eventPhase(event);

  // Per-station allocation rows (one wheel slice each; same stock may repeat),
  // for the inline editor under each station.
  const allocationsByStation = useMemo(() => {
    const m = new Map<number, XkEventStockStation[]>();
    for (const a of allocationsQ.data?.items ?? []) {
      const g = m.get(a.stationId);
      if (g) g.push(a);
      else m.set(a.stationId, [a]);
    }
    return m;
  }, [allocationsQ.data]);

  return (
    <div
      ref={rowRef}
      className={cn(
        'relative rounded-lg border bg-[var(--color-bg-surface)] transition-all',
        selected ? 'border-[var(--color-accent-ink)]' : 'border-[var(--color-border-subtle)]',
        // Candidate drop zone while dragging; strong highlight when hovered.
        dragging && !isOver && 'border-dashed border-[var(--color-accent-ink)]/60',
        isOver && 'border-[var(--color-accent-ink)] ring-2 ring-[var(--color-xk-accent)] bg-[var(--color-xk-accent)]/5',
      )}
    >
      <div ref={setNodeRef}>
        {/* Drop affordance overlay — appears only over the hovered event. */}
        {isOver && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-[var(--color-xk-accent)]/10">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-xk-accent)] px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-on-accent)] shadow-lg">
              <MousePointerClick className="h-3.5 w-3.5" />
              {t('planner.drop.here')}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 p-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
            aria-label={open ? t('planner.events.collapse') : t('planner.events.expand')}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => onSelect(event.id)}
            className="min-w-0 flex-1 text-left"
          >
            <p className="truncate font-semibold text-[var(--color-xk-lavender)]">{event.name}</p>
            <p className="truncate text-[11px] text-[var(--color-xk-text-muted)]">
              {t(`planner.phase.${phase}`)}
              {event.address ? ` · ${event.address}` : ''}
            </p>
          </button>
          {/* Task 4.5 — which game runs here and how many slots, without
              opening anything. */}
          <GameGlanceChip eventId={event.id} />
          {/* Fuera del `<button>` que despliega la fila: un control dentro de
              otro es HTML inválido, y al pulsar una etiqueta se plegaría la
              fila en vez de abrir el selector. */}
          {etiquetas}
          {phase === 'live' && onOpenProperties ? (
            /* La misma píldora que la fila de estación, aunque aquí sólo lleve
               una acción: así las dos filas del planner hablan igual. */
            <IconButtonGroup label={t('planner.events.actions')}>
              <IconButtonGroupItem
                label={t('planner.properties.open')}
                icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
                onClick={() => onOpenProperties(event.id)}
              />
            </IconButtonGroup>
          ) : null}
          {!hideOpenConfig && (
          <Link
            to={`/projects/${groupId}/events/${event.id}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[var(--color-xk-border)] px-2.5 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-accent-ink)]"
          >
            <ExternalLink className="h-3 w-3" />
            {t('planner.events.openConfig')}
          </Link>
          )}
        </div>

        {open && (
          <div className="space-y-3 border-t border-[var(--color-border-subtle)] p-3">
            <Section icon={<Package className="h-3.5 w-3.5" />} label={t('planner.events.xotskis')}>
              {allocations.length === 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Empty text={t('planner.events.xotskiRollupEmpty')} />
                  {onRepartirTodo ? (
                    /*
                      Sin arrastrar: el arrastre sigue estando, pero no puede ser
                      el ÚNICO camino — con teclado o si no engancha, la estación
                      se quedaba sin premios y el invitado sin nada que ganar.
                    */
                    <button
                      type="button"
                      onClick={() => void onRepartirTodo(event.id)}
                      className="rounded-md border border-[var(--color-border-default)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--color-fg-primary)] hover:border-[var(--color-accent-ink)]"
                    >
                      {t('planner.spreadAll.cta')}
                    </button>
                  ) : null}
                </div>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {groupByStock(allocations).map((group) => (
                    <AllocationSummaryChip
                      key={group[0].stockId}
                      group={group}
                      stock={stockMap.get(group[0].stockId)}
                    />
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={<UserPlus className="h-3.5 w-3.5" />} label={t('planner.events.reporters')}>
              {reporters.length === 0 ? (
                <Empty text={t('planner.events.dropReporterHint')} />
              ) : (
                <ul className="flex gap-3 overflow-x-auto pb-1 scrollbar-thin">
                  {reporters.map((r) => (
                    <ReporterPill key={r.id} reporter={r} eventId={event.id} brandId={brandId} />
                  ))}
                </ul>
              )}
            </Section>

            <Section
              icon={<QrCode className="h-3.5 w-3.5" />}
              label={t('planner.events.stationCount', { count: String(stations.length) })}
            >
              {stations.length === 0 ? (
                <Empty text={t('planner.events.dropStationHint')} />
              ) : (
                /* Dos columnas sólo si CABEN: el corte va por el ancho del
                    contenedor, no por el de la ventana, porque con el raíl
                    derecho abierto la tarjeta del evento se estrecha aunque la
                    ventana sea ancha, y a media tarjeta el nombre de la
                    estación se queda en una letra por línea. */
                <div className="@container">
                <ul className="grid grid-cols-1 gap-2 @4xl:grid-cols-2">
                  {stations.map((s) => (
                    <StationCard
                      key={s.id}
                      station={s}
                      eventId={event.id}
                      brandId={brandId}
                      companyId={event.companyId ?? 0}
                      isLast={stations.length <= 1}
                      allocations={allocationsByStation.get(s.id) ?? []}
                      stockMap={stockMap}
                    />
                  ))}
                </ul>
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Group allocation rows by stockId, preserving first-seen order. The planner
 * query returns one row per (station / wheel-slice) allocation, so the same
 * xotski type can appear many times; the planner collapses them into one chip
 * per type with the summed quantity. Per-row quantities stay editable at the
 * station detail (where each row's rating / wheel weight also lives).
 */
function groupByStock(rows: XkEventStockStation[]): XkEventStockStation[][] {
  const map = new Map<number, XkEventStockStation[]>();
  for (const r of rows) {
    const g = map.get(r.stockId);
    if (g) g.push(r);
    else map.set(r.stockId, [r]);
  }
  return [...map.values()];
}

/**
 * Read-only rollup chip — ONE per stock type, showing the summed quantity
 * across this event's stations and a "×N" badge when the type spans several
 * allocation rows (slices). Editing now lives per-station (StationCard), so
 * this is purely a what's-on-this-event summary.
 */
function AllocationSummaryChip({
  group,
  stock,
}: {
  group: XkEventStockStation[];
  stock: StockLite | undefined;
}) {
  const t = useT();
  const label = stock?.name ?? `#${group[0].stockId}`;
  const totalQty = group.reduce((s, a) => s + (a.quantity ?? 0), 0);
  return (
    <li
      className="flex items-center gap-1.5 rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] py-1 pl-1 pr-2.5"
      title={t('planner.alloc.groupedHint', { n: String(group.length), total: String(totalQty) })}
    >
      {stock?.image ? (
        <img src={resolveImageUrl(stock.image)} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />
      ) : (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-surface)]">
          <Package className="h-3.5 w-3.5 text-[var(--color-xk-text-muted)]" />
        </span>
      )}
      <span className="max-w-[96px] truncate text-[11px] font-semibold text-[var(--color-xk-lavender)]">
        {label}
      </span>
      <span className="min-w-[1.25rem] text-center text-[11px] font-black tabular-nums text-[var(--color-xk-lavender)]">
        {totalQty}
      </span>
      {group.length > 1 && (
        <span className="rounded-full bg-[var(--color-bg-surface)] px-1.5 text-[9px] font-bold tabular-nums text-[var(--color-xk-text-muted)]">
          ×{group.length}
        </span>
      )}
    </li>
  );
}

/** A reporter chip: round avatar + name/email + confirmation status + remove. */
function ReporterPill({
  reporter,
  eventId,
  brandId,
}: {
  reporter: ReporterListRow;
  eventId: number;
  brandId: number;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const confirmed = reporter.termsAccepted;
  const statusKey =
    reporter.linkState === 'revoked'
      ? 'planner.reporters.statusRevoked'
      : reporter.linkState === 'expired'
        ? 'planner.reporters.statusExpired'
        : confirmed
          ? 'planner.reporters.statusConfirmed'
          : 'planner.reporters.statusPending';
  const tone = confirmed
    ? 'var(--color-xk-accent)'
    : reporter.linkState === 'active'
      ? 'var(--color-warn)'
      : 'var(--color-error)';

  const remove = useMutation({
    mutationFn: () => reporterAdminApi.remove(reporter.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reporters.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reporters.byBrand(brandId) });
      toast({ tone: 'success', title: t('planner.reporters.removed') });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('planner.reporters.removeFailed'), description: e.message }),
  });

  return (
    <li className="relative flex w-24 shrink-0 flex-col items-center gap-1 text-center">
      <button
        type="button"
        disabled={remove.isPending}
        onClick={() => remove.mutate()}
        aria-label={t('planner.reporters.remove')}
        className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-[var(--color-xk-border)] bg-[var(--color-bg-surface)] text-[var(--color-error)] transition-colors hover:border-[var(--color-error)] disabled:opacity-40"
      >
        <X className="h-3 w-3" />
      </button>
      <ReporterAvatar name={reporter.name} email={reporter.email} />
      <span className="w-full truncate text-[11px] font-semibold text-[var(--color-xk-lavender)]">
        {reporter.name || reporter.email}
      </span>
      <span
        className="rounded-full px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider"
        style={{ color: tone, border: `1px solid ${tone}` }}
      >
        {t(statusKey)}
      </span>
    </li>
  );
}

function Section({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <p className="rounded-md border border-dashed border-[var(--color-xk-border)] px-2 py-1.5 text-[11px] text-[var(--color-xk-text-muted)]">
      {text}
    </p>
  );
}
